import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { SHARED_MODULES } from '../../../shared-imports';
import { TranslatePipe } from 'src/app/i18n/translate.pipe';
import { PushNotificationService } from 'src/app/services/push-notification.service';

// Shown once, right at the deliberate "onboarding is done" tap (ConfirmAccountSetupComponent /
// IosAllSetComponent's navigateToDashboard()) — the one moment the user has just finished setting
// up their account and is about to see the real app for the first time. This replaces the old
// behavior of firing the native OS permission prompt automatically on cold start, before signup,
// with zero context — see push-notification.service.ts's init()/requestPermissionNow() split.
@Component({
  selector: 'app-enable-notifications-prompt',
  standalone: true,
  imports: [SHARED_MODULES, TranslatePipe],
  template: `
    <p-dialog [(visible)]="visible" [modal]="true" [closable]="false" [showHeader]="false"
      [dismissableMask]="false" [style]="{ width: '26rem', 'max-width': '92vw' }" styleClass="enp-dialog">
      <div class="enp-body">
        <div class="enp-icon"><i class="pi pi-bell"></i></div>
        <h3 class="enp-title">{{ 'pushPrompt.title' | t }}</h3>
        <p class="enp-desc">{{ 'pushPrompt.body' | t }}</p>
        <div class="enp-actions">
          <button pButton type="button" class="p-button-text" [label]="'pushPrompt.notNow' | t"
            [disabled]="requesting" (click)="respond(false)"></button>
          <button pButton type="button" icon="pi pi-bell" [label]="'pushPrompt.enable' | t"
            [loading]="requesting" (click)="respond(true)"></button>
        </div>
      </div>
    </p-dialog>
  `,
  styles: [`
    .enp-body { text-align: center; padding: .5rem .25rem; }
    .enp-icon {
      width: 3.5rem; height: 3.5rem; margin: 0 auto .75rem; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      background: color-mix(in srgb, var(--primary-color) 12%, transparent);
      color: var(--primary-color); font-size: 1.5rem;
    }
    .enp-title { margin: 0 0 .5rem; font-size: 1.1rem; font-weight: 700; color: var(--text-color); }
    .enp-desc { margin: 0 0 1.25rem; font-size: .9rem; line-height: 1.5; color: var(--text-color-secondary); }
    .enp-actions { display: flex; justify-content: center; gap: .5rem; }
  `],
})
export class EnableNotificationsPromptComponent {
  private readonly pushNotificationService = inject(PushNotificationService);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();

  requesting = false;

  async respond(enable: boolean): Promise<void> {
    if (enable) {
      this.requesting = true;
      try {
        await this.pushNotificationService.requestPermissionNow();
      } finally {
        this.requesting = false;
      }
    }
    // Remember the user has been asked once, on either choice — the app-wide auto-prompt on login
    // (AppComponent.maybePromptForPushPermission) reads this same key so it never re-nags. The
    // per-user Account Details row stays available for anyone who wants to enable it later.
    try { localStorage.setItem('ll_push_prompt_seen', '1'); } catch { /* private mode / storage off */ }
    this.visible = false;
    this.visibleChange.emit(false);
    this.done.emit();
  }
}
